import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { TownMap } from "@/components/TownMap";
import api, { formatApiError } from "@/lib/api";
import { Loader2, ImagePlus, X, MapPin, ChevronLeft, AlertOctagon, AlertTriangle, Award, Heart } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { localityCoords, ISRAEL_CENTER } from "@/lib/localityUtils";

export default function CreateReportPage() {
  const navigate = useNavigate();
  const { language } = useI18n();
  const { user } = useAuth();
  const isAr = language === "ar"; const isHe = language === "he";
  const L = (ar, he, en) => isAr ? ar : isHe ? he : en;

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "general", address: "" });
  const [imageUrl, setImageUrl] = useState(null);
  const [location, setLocation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB image"); return; }
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error(L("الرجاء تعبئة العنوان والوصف.", "אנא מלא כותרת ותיאור.", "Please fill the title and description."));
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, image_url: imageUrl, lat: location?.lat, lng: location?.lng };
      const { data } = await api.post("/reports", payload);
      toast.success(L("تم نشر البلاغ", "הדיווח פורסם", "Report published"));
      navigate(`/reports/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const mapCenter = (() => {
    if (location) return [location.lat, location.lng];
    const c = localityCoords(user?.city_id);
    if (c) return [c.lat, c.lng];
    return [ISRAEL_CENTER.lat, ISRAEL_CENTER.lng];
  })();

  const catIcon = {
    emergency: { Icon: AlertOctagon, bg: "#FEE2E2", color: "#DC2626" },
    general:   { Icon: AlertTriangle, bg: "#EDE7F6", color: "#673AB7" },
    announcement: { Icon: Award, bg: "#CFFAFE", color: "#06B6D4" },
    project:   { Icon: Heart, bg: "#D1FAE5", color: "#10B981" },
  };

  return (
    <MobileShell
      topBar={
        <div className="h-14 flex items-center gap-2 px-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="create-back-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="font-extrabold text-[20px] text-[var(--te-text)]">
            {L("بلاغ جديد", "דיווח חדש", "New report")}
          </h1>
        </div>
      }
    >
      <div className="px-4 pt-4 pb-6">
        <form onSubmit={submit} className="space-y-5">
          {/* Category chips */}
          <div>
            <label className="te-eyebrow block mb-2">{L("النوع", "סוג", "Category")}</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => {
                const meta = catIcon[c.id] || catIcon.general;
                const Icon = meta.Icon;
                const active = form.category === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`create-cat-${c.id}`}
                    onClick={() => setForm((f) => ({ ...f, category: c.id }))}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-semibold border transition-colors ${
                      active
                        ? "bg-[var(--te-primary)] text-white border-[var(--te-primary)]"
                        : "bg-white text-[var(--te-text)] border-[var(--te-divider)] hover:bg-[var(--te-bg-soft)]"
                    }`}
                  >
                    <Icon size={13} style={!active ? { color: meta.color } : {}} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="te-label">{L("العنوان", "כותרת", "Title")}</label>
            <input
              className="te-input"
              required
              maxLength={140}
              value={form.title}
              onChange={onChange("title")}
              placeholder={L("مثال: حفرة على الشارع الرئيسي", "לדוגמה: בור ברחוב הראשי", "e.g., Pothole on Main St")}
              data-testid="create-title-input"
              dir="auto"
            />
          </div>

          {/* Description */}
          <div>
            <label className="te-label">{L("الوصف", "תיאור", "Description")}</label>
            <textarea
              className="te-input min-h-[120px] resize-y"
              required
              maxLength={4000}
              value={form.description}
              onChange={onChange("description")}
              placeholder={L("اشرح ما يحدث، أوقاته، وأي معلومات سلامة.", "תאר מה קורה, מתי, וכל פרט בטיחות.", "Describe what's happening, when, and any safety info.")}
              data-testid="create-description-input"
              dir="auto"
            />
          </div>

          {/* Address */}
          <div>
            <label className="te-label">
              {L("العنوان (اختياري)", "כתובת (אופציונלי)", "Address (optional)")}
            </label>
            <input
              className="te-input"
              value={form.address}
              onChange={onChange("address")}
              placeholder={L("شارع، رقم", "רחוב, מספר", "Street, number")}
              data-testid="create-address-input"
              dir="auto"
            />
          </div>

          {/* Photo */}
          <div>
            <label className="te-label">
              {L("صورة (اختياري، 5 ميغا كحد أقصى)", "תמונה (אופציונלי, עד 5MB)", "Photo (optional, max 5MB)")}
            </label>
            {imageUrl ? (
              <div className="relative inline-block">
                <img src={imageUrl} alt="" className="rounded-2xl max-h-56 border border-[var(--te-divider)]" />
                <button
                  type="button"
                  onClick={() => setImageUrl(null)}
                  data-testid="create-remove-image-btn"
                  className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-sm border border-[var(--te-divider)]"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label
                className="te-btn-secondary inline-flex items-center gap-2 cursor-pointer"
                data-testid="create-upload-image-btn"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                {uploading ? L("جارٍ الرفع…", "מעלה…", "Uploading…") : L("ارفع صورة", "העלה תמונה", "Upload image")}
                <input type="file" accept="image/*" className="hidden" onChange={onFileChange} />
              </label>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="te-label flex items-center gap-2">
              <MapPin size={14} /> {L("الموقع (اضغط على الخريطة)", "מיקום (לחץ על המפה)", "Location (tap the map)")}
            </label>
            <TownMap onClick={setLocation} selected={location} center={mapCenter} zoom={location ? 14 : 10} height={260} />
            {location && (
              <div className="text-xs text-[var(--te-text-secondary)] mt-2 ltr text-left" data-testid="create-location-coords">
                Pinned: {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="te-btn-secondary flex-1 justify-center"
              data-testid="create-cancel-btn"
            >
              {L("إلغاء", "ביטול", "Cancel")}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="te-btn-primary flex-1 justify-center"
              data-testid="create-submit-btn"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {L("نشر البلاغ", "פרסם דיווח", "Publish report")}
            </button>
          </div>
        </form>
      </div>
    </MobileShell>
  );
}
